"use client";

import NumberFlow from "@number-flow/react";
import { ArrowRight, Clapperboard, Play } from "lucide-react";
import { useRouter } from "next/navigation";
import { FacelessGeneratorForm } from "@/components/create/FacelessGeneratorForm";
import {
  type FacelessGeneratorDraft,
  writeFacelessGeneratorDraft,
} from "@/components/create/faceless-generator-draft";
import {
  resolveDemoVideoPosterUrl,
  resolveDemoVideoUrl,
} from "@/lib/media/demo-video-url";
import { cn } from "@/lib/utils";
import {
  thumbMediaClassName,
  thumbMediaReadyClassName,
  thumbMediaWrapClassName,
  thumbSkeletonClassName,
} from "../editor/thumbMediaStyles";
import { useEffect, useRef, useState } from "react";

const generatorReturnPath = "/app/create/faceless-video";

const INITIAL_GENERATED_COUNT = 34906;
const GENERATED_COUNT_INTERVAL_MS = 5000;
const facelessPreviewPosterUrl = resolveDemoVideoPosterUrl("documentary.mp4");

export function LandingFacelessGeneratorSection() {
  const router = useRouter();
  const [generatedCount, setGeneratedCount] = useState(INITIAL_GENERATED_COUNT);
  const [previewPhase, setPreviewPhase] = useState<
    "loading" | "ready" | "error"
  >("loading");
  const previewWrapRef = useRef<HTMLDivElement | null>(null);
  const previewVideoRef = useRef<HTMLVideoElement | null>(null);
  const [shouldLoadPreviewVideo, setShouldLoadPreviewVideo] = useState(false);

  useEffect(() => {
    const id = window.setInterval(() => {
      setGeneratedCount((n) => n + 1);
    }, GENERATED_COUNT_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, []);

  useEffect(() => {
    const element = previewWrapRef.current;

    if (!element) {
      return;
    }

    if (typeof IntersectionObserver === "undefined") {
      const frameId = window.requestAnimationFrame(() => {
        setShouldLoadPreviewVideo(true);
      });
      return () => window.cancelAnimationFrame(frameId);
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          setShouldLoadPreviewVideo(true);
          observer.disconnect();
        }
      },
      { rootMargin: "520px 0px" },
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!shouldLoadPreviewVideo) {
      return;
    }

    const video = previewVideoRef.current;
    if (!video) {
      return;
    }

    const playPromise = video.play();
    if (playPromise && typeof playPromise.catch === "function") {
      playPromise.catch(() => {
        // Ignore autoplay blocking (e.g., Firefox strict autoplay settings).
      });
    }
  }, [shouldLoadPreviewVideo]);

  function handleSubmit(draft: FacelessGeneratorDraft) {
    writeFacelessGeneratorDraft(draft);
    router.push(`/login?next=${encodeURIComponent(generatorReturnPath)}`);
  }

  function handleScriptGenerationRequest(draft: FacelessGeneratorDraft) {
    writeFacelessGeneratorDraft(draft);
    router.push(`/login?next=${encodeURIComponent(generatorReturnPath)}`);
  }

  return (
    <section id="generator" className="py-20 md:py-28">
      <div className="mx-auto w-[min(1240px,calc(100vw-32px))] md:w-[min(1240px,calc(100vw-56px))]">
        <div className="grid gap-10 lg:grid-cols-[minmax(0,1.05fr)_minmax(320px,0.95fr)] lg:items-start">
          <div className="text-center lg:text-left">
            <h2 className="max-w-3xl font-display text-[clamp(2rem,4vw,3.5rem)] leading-[0.98] tracking-[-0.05em] text-white">
              <span className="text-transparent bg-gradient-to-r from-violet-300 via-fuchsia-400 to-rose-400 bg-clip-text">
                AI Faceless Video
              </span>{" "}
              Generator
            </h2>
            <div className="mx-auto mt-6 inline-flex max-w-full flex-wrap items-center gap-2.5 rounded-full border border-fuchsia-400/22 bg-white/[0.045] px-3.5 py-2 shadow-[0_0_0_1px_rgba(255,255,255,0.04),0_12px_42px_rgba(168,85,247,0.14)] backdrop-blur-md sm:gap-3 sm:px-4 sm:py-2.5 lg:mx-0">
              <span aria-hidden className="relative flex size-2 shrink-0">
                <span className="absolute inline-flex size-full animate-ping rounded-full bg-fuchsia-400/35 motion-reduce:animate-none" />
                <span className="relative inline-flex size-2 rounded-full bg-fuchsia-400 shadow-[0_0_14px_rgba(232,121,249,0.65)]" />
              </span>
              <Clapperboard
                aria-hidden
                className="size-3.5 shrink-0 text-fuchsia-200/90 sm:size-4"
              />
              <p className="min-w-0 text-sm leading-snug sm:text-[0.9375rem]">
                <NumberFlow
                  className="inline tabular-nums font-display text-lg font-semibold tracking-[-0.04em] text-white sm:text-xl"
                  format={{ useGrouping: true }}
                  locales="en-US"
                  value={generatedCount}
                />
                <span className="font-semibold text-fuchsia-200/95">+</span>
                <span className="font-medium text-white/55">
                  {" "}
                  videos generated
                </span>
              </p>
            </div>

            <div className="mt-9 grid gap-4 rounded-[30px] border border-white/10 bg-[radial-gradient(circle_at_88%_0%,rgba(217,70,239,0.14),transparent_28%),linear-gradient(180deg,rgba(255,255,255,0.045),rgba(255,255,255,0.018)),rgba(12,12,18,0.94)] p-4 shadow-[0_30px_90px_rgba(0,0,0,0.28)] sm:p-5">
              <FacelessGeneratorForm
                onScriptGenerationRequest={handleScriptGenerationRequest}
                onSubmit={handleSubmit}
                submitLabel="Generate"
                submittingLabel="Saving your setup..."
              />
            </div>
          </div>

          <aside className="rounded-[32px] border border-white/10 bg-[radial-gradient(circle_at_top_right,rgba(138,63,252,0.18),transparent_30%),rgba(12,12,12,0.95)] p-5 shadow-[0_24px_76px_rgba(0,0,0,0.32)] max-lg:mx-auto max-lg:w-full max-lg:max-w-[560px] lg:sticky lg:top-[max(88px,calc(112px+((100vh-112px-724px)/2)))] lg:self-start">
            <div className="mb-4 flex items-center justify-between gap-4">
              <div className="inline-flex min-h-7 w-fit items-center gap-2 rounded-full border border-white/[0.08] px-[11px] text-[0.68rem] font-extrabold uppercase tracking-[0.14em] text-white">
                <span className="size-[7px] rounded-full bg-[#b58cf8]" />
                <span>Preview</span>
              </div>
              <div className="inline-flex items-center gap-2 text-sm font-semibold text-white/56">
                <Play className="size-4 text-fuchsia-200" />
                Faceless format
              </div>
            </div>

            <div className="relative mx-auto aspect-[9/16] w-[min(100%,360px,calc((100vh-260px)*0.5625))] overflow-hidden rounded-[30px] bg-black">
              <div
                ref={previewWrapRef}
                aria-hidden
                className={cn(thumbMediaWrapClassName, "absolute inset-0")}
              >
                {previewPhase === "loading" && !facelessPreviewPosterUrl ? (
                  <div aria-hidden className={thumbSkeletonClassName} />
                ) : null}
                <video
                  ref={previewVideoRef}
                  className={cn(
                    "absolute inset-0 size-full object-cover",
                    thumbMediaClassName,
                    "opacity-100",
                    previewPhase === "ready" && thumbMediaReadyClassName,
                  )}
                  loop
                  muted
                  onError={() => setPreviewPhase("error")}
                  onCanPlay={() => setPreviewPhase("ready")}
                  onLoadedData={() => setPreviewPhase("ready")}
                  onLoadedMetadata={() => setPreviewPhase("ready")}
                  playsInline
                  poster={facelessPreviewPosterUrl}
                  preload={shouldLoadPreviewVideo ? "metadata" : "none"}
                  src={
                    shouldLoadPreviewVideo
                      ? resolveDemoVideoUrl("documentary.mp4")
                      : undefined
                  }
                />
              </div>
              <div className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/72 via-black/18 to-transparent p-5">
                <div className="flex items-center justify-between gap-3 rounded-2xl border border-white/10 bg-black/30 px-4 py-3 text-sm text-white/78 backdrop-blur-md">
                  <span>Ready after signup</span>
                  <ArrowRight className="size-4 text-fuchsia-200" />
                </div>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
}
